/**
 * Buyer and supplier statements, and the organisation's billing details.
 *
 * A draft is worked out on the server every time it is read; finalising
 * numbers it and freezes it. Money arrives as integer pence (`*_pence`), so
 * nothing here adds up floats.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

type S = components['schemas'];
export type Statement = S['Statement'];
export type StatementSummary = S['StatementSummary'];
export type StatementKind = S['StatementKind'];
export type BillingSettings = S['BillingSettings'];
export type PartyDetails = S['PartyDetails'];
export type SummaryLine = S['SummaryLine'];
export type Totals = S['Totals'];

export function useBillingSettings(enabled = true) {
  return useQuery({
    queryKey: ['statements', 'billing'],
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/billing', {}), 'BillingSettings'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useSaveBillingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BillingSettings) =>
      unwrap(await api.PUT('/api/v1/settings/billing', { body }), 'BillingSettings'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statements'] }),
  });
}

/** Stored statements, and (staff) a draft per party for `period`. */
export function useStatements(query: { kind?: StatementKind; party_id?: string; period?: string }, enabled = true) {
  return useQuery({
    queryKey: ['statements', 'list', query],
    queryFn: async () => unwrap(await api.GET('/api/v1/statements', { params: { query } }), 'StatementList'),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDraftStatement(kind: StatementKind | null, partyId: string | null, period: string | null) {
  return useQuery({
    queryKey: ['statements', 'draft', kind, partyId, period],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/statements/draft', {
          params: { query: { kind: kind!, party_id: partyId!, period: period! } },
        }),
        'Statement',
      ),
    enabled: !!kind && !!partyId && !!period,
  });
}

export function useStatement(id: string | null) {
  return useQuery({
    queryKey: ['statements', 'one', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/statements/{id}', { params: { path: { id: id! } } }), 'Statement'),
    enabled: !!id,
  });
}

export function useFinaliseStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S['FinaliseBody']) =>
      unwrap(await api.POST('/api/v1/statements/finalise', { body }), 'Statement'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statements'] }),
  });
}

export function useMarkPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paidOn }: { id: string; paidOn: string | null }) =>
      unwrap(
        await api.POST('/api/v1/statements/{id}/paid', { params: { path: { id } }, body: { paid_on: paidOn } }),
        'Statement',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statements'] }),
  });
}

export { downloadStatementCsv } from './upload';

/** Pence as pounds: "£1,234.56", "-£11.00". */
export function formatPence(p: number): string {
  const sign = p < 0 ? '-' : '';
  const abs = Math.abs(p);
  const pounds = Math.floor(abs / 100).toLocaleString('en-GB');
  return `${sign}£${pounds}.${String(abs % 100).padStart(2, '0')}`;
}

/** "October 2026" for "2026-10". */
export function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** The month before `period`, or before this month in London. */
export function previousMonth(period?: string): string {
  let y: number;
  let m: number;
  if (period) {
    [y, m] = period.split('-').map(Number) as [number, number];
  } else {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit' })
      .formatToParts(new Date());
    y = Number(parts.find((p) => p.type === 'year')?.value);
    m = Number(parts.find((p) => p.type === 'month')?.value);
  }
  const prev = m === 1 ? [y - 1, 12] : [y, m - 1];
  return `${prev[0]}-${String(prev[1]).padStart(2, '0')}`;
}

/** "20%" for 2000 basis points. */
export function formatRateBp(bp: number): string {
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}
